import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Wallet, 
  Search, 
  RefreshCw, 
  ArrowUpCircle, 
  ArrowDownCircle,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  Bike,
  Car,
  Eye,
  Plus,
  Minus,
  Loader
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import WalletAdjustmentModal from '../../components/admin/WalletAdjustmentModal';

const AdminWalletManagement = () => {
  const [overview, setOverview] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [processingWithdrawal, setProcessingWithdrawal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const api = (await import('../../utils/api')).default;
      
      // Fetch overview
      const overviewRes = await api.get('/admin/wallet/overview');
      setOverview(overviewRes.data.overview);
      setWithdrawals(overviewRes.data.pendingWithdrawals || []);
      
      // Fetch drivers with wallets
      const driversRes = await api.get('/admin/wallet/drivers?limit=100');
      setDrivers(driversRes.data.drivers || []);
      
      // Fetch recent transactions
      const txRes = await api.get('/admin/wallet/transactions?limit=50');
      setTransactions(txRes.data.transactions || []);
      
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load wallet data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (vehicleFilter !== 'all') params.append('vehicle_type', vehicleFilter);
      
      const res = await api.get(`/admin/wallet/drivers?${params.toString()}`);
      setDrivers(res.data.drivers || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Search failed',
        variant: 'destructive',
      });
    }
  };

  const handleProcessWithdrawal = async (withdrawalId, action) => {
    if (action === 'reject' && !rejectionReason.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a rejection reason',
        variant: 'destructive',
      });
      return;
    }

    setProcessingWithdrawal(withdrawalId);
    try {
      const api = (await import('../../utils/api')).default;
      await api.put(`/admin/wallet/withdrawals/${withdrawalId}`, {
        action,
        rejectionReason: action === 'reject' ? rejectionReason : null
      });

      toast({
        title: 'Success',
        description: `Withdrawal ${action === 'approve' ? 'approved' : 'rejected'} successfully`,
      });

      setShowRejectModal(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to process withdrawal',
        variant: 'destructive',
      });
    } finally {
      setProcessingWithdrawal(null);
    }
  };

  const openAdjustModal = (driver) => {
    setSelectedDriver(driver);
    setShowAdjustModal(true);
  };

  const filteredDrivers = drivers.filter(d => {
    if (vehicleFilter === 'bike') {
      return d.vehicleType?.toLowerCase().includes('bike');
    } else if (vehicleFilter === 'car') {
      return !d.vehicleType?.toLowerCase().includes('bike');
    }
    return true;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader className="h-12 w-12 animate-spin text-pink-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Wallet Management</h1>
            <p className="text-gray-600 mt-1">Manage driver and biker wallets</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Drivers/Bikers</p>
                <p className="text-3xl font-bold text-gray-900">{overview?.totalDrivers || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Wallet Balance</p>
                <p className="text-3xl font-bold text-green-600">K{overview?.totalWalletBalance?.toFixed(2) || '0.00'}</p>
              </div>
              <Wallet className="h-8 w-8 text-green-600" />
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pending Withdrawals</p>
                <p className="text-3xl font-bold text-orange-600">K{overview?.totalPendingWithdrawals?.toFixed(2) || '0.00'}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-600" />
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Withdrawal Requests</p>
                <p className="text-3xl font-bold text-purple-600">{overview?.pendingWithdrawalCount || 0}</p>
              </div>
              <ArrowUpCircle className="h-8 w-8 text-purple-600" />
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          {[
            { id: 'overview', label: 'All Wallets', icon: Wallet },
            { id: 'withdrawals', label: 'Withdrawal Requests', icon: ArrowUpCircle },
            { id: 'transactions', label: 'Transaction History', icon: Clock }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? 'border-pink-600 text-pink-600' 
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.id === 'withdrawals' && withdrawals.length > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {withdrawals.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-64">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by name or phone..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      className="pl-10"
                    />
                  </div>
                </div>
                <select
                  value={vehicleFilter}
                  onChange={(e) => setVehicleFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500"
                >
                  <option value="all">All Vehicles</option>
                  <option value="car">Cars Only</option>
                  <option value="bike">Bikes Only</option>
                </select>
                <Button onClick={handleSearch}>Search</Button>
              </div>
            </Card>

            {/* Drivers List */}
            <Card className="overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Driver</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Balance</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Earnings</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Commission</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredDrivers.map((driver) => (
                    <tr key={driver.userId} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{driver.name}</p>
                          <p className="text-sm text-gray-500">{driver.phone}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {driver.vehicleType?.toLowerCase().includes('bike') ? (
                            <Bike className="h-4 w-4 text-orange-600" />
                          ) : (
                            <Car className="h-4 w-4 text-blue-600" />
                          )}
                          <span className="text-sm text-gray-600">{driver.vehicleType}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          driver.approvalStatus === 'approved' 
                            ? 'bg-green-100 text-green-800'
                            : driver.approvalStatus === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {driver.approvalStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${driver.walletBalance > 0 ? 'text-green-600' : 'text-gray-900'}`}>
                          K{driver.walletBalance?.toFixed(2) || '0.00'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        K{driver.totalEarnings?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 text-right text-red-600">
                        -K{driver.totalCommissionPaid?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => openAdjustModal(driver)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Adjust
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredDrivers.length === 0 && (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No drivers found</p>
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'withdrawals' && (
          <div className="space-y-4">
            {withdrawals.length === 0 ? (
              <Card className="p-12 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <p className="text-gray-600">No pending withdrawal requests</p>
              </Card>
            ) : (
              <Card className="overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Driver</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Method</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Requested</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {withdrawals.map((withdrawal) => (
                      <tr key={withdrawal.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{withdrawal.driverName}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-orange-600">
                            K{withdrawal.amount?.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {withdrawal.paymentMethod}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(withdrawal.requestedAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleProcessWithdrawal(withdrawal.id, 'approve')}
                              disabled={processingWithdrawal === withdrawal.id}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowRejectModal(withdrawal.id)}
                              disabled={processingWithdrawal === withdrawal.id}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'transactions' && (
          <Card className="overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Driver</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Amount</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Balance After</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transactions.map((tx, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(tx.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{tx.driverName || 'Unknown'}</p>
                      <p className="text-sm text-gray-500">{tx.driverPhone}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        tx.type.includes('credit') || tx.type === 'deposit'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {tx.type.includes('credit') || tx.type === 'deposit' ? (
                          <ArrowDownCircle className="h-3 w-3" />
                        ) : (
                          <ArrowUpCircle className="h-3 w-3" />
                        )}
                        {tx.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={tx.amount > 0 ? 'text-green-600' : 'text-red-600'}>
                        {tx.amount > 0 ? '+' : ''}K{tx.amount?.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      K{tx.balanceAfter?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                      {tx.reason || tx.description || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {transactions.length === 0 && (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No transactions found</p>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Wallet Adjustment Modal */}
      <WalletAdjustmentModal
        isOpen={showAdjustModal}
        onClose={() => {
          setShowAdjustModal(false);
          setSelectedDriver(null);
        }}
        driver={selectedDriver}
        onSuccess={fetchData}
      />

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Reject Withdrawal</h3>
            <p className="text-gray-600 mb-4">Please provide a reason for rejecting this withdrawal request.</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
              rows={3}
            />
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectionReason('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleProcessWithdrawal(showRejectModal, 'reject')}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={!rejectionReason.trim()}
              >
                Reject Withdrawal
              </Button>
            </div>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminWalletManagement;
